import React from "react"
import { FeatureActionEnum, FeatureEnum } from "@gap/backend/app-services/feature/models/FeatureEnum"
import { FeatureToggleEnum } from "@gap/backend/app-services/feature-toggle/models/FeatureToggleEnum"
import { ChargebeeFeatureId } from "@gap/utils/chargebee/chargebee-features"
import { namingConfig } from "@gap/utils/config/naming-config"
import { OrgTypeEnum } from "@gap/backend/app-services/org/models/OrgTypeEnum"
import { canSeeWorkItems, hasFeatureToggleBypass, RouteType, StageEnum } from "@gap/utils/utils"
import Mindsets from "features/mindsets/Mindsets"
import DevSettings from "features/dev-settings/DevSettings"
import AccessDenied from "features/access-denied/AccessDenied"
import { UserOrgDto } from "@gap/backend/app-services/user-org/models/UserOrgDto"
import UserManagement from "features/user-management/UserManagement"
import { UserDto } from "@gap/backend/app-services/user/models/UserDto"
import { queuesPermission } from "features/queues/queuesPermission"

const Clients = React.lazy(() => import('features/clients/ClientRouter'))
const ValueGapCalculator = React.lazy(() => import('features/vgc/VgcRouter'))
const Proposals = React.lazy(() => import('features/proposals/ProposalsRouter'))
const Services = React.lazy(() => import('features/services/Services'))
const MyProfileView = React.lazy(() => import('features/my-profile/MyProfileView'))
const ProcessList = React.lazy(() => import('features/processes/ProcessListView'))
const OrgList = React.lazy(() => import('features/org/list/OrgList'))
const Role = React.lazy(() => import("features/roles/Role"))
const OrgEdit = React.lazy(() => import("features/org/edit/OrgEdit"))
const DevSandbox = React.lazy(() => import("features/dev-sandbox/DevSandbox"))
const BlueprintOrgTypeView = React.lazy(() => import("features/blueprints/BlueprintOrgTypeView"))
const WorkItemView = React.lazy(() => import("features/work-items/WorkItemView"))
const DocRouter = React.lazy(() => import("features/docs/DocRouter"))
const DeliveryNotes = React.lazy(() => import("features/delivery-notes/DeliveryNotes"))
const BackgroundJobListView = React.lazy(() => import("background-jobs/BackgroundJobListView"))
const AIPromptView = React.lazy(() => import("features/ai-prompt/AIPromptView"))
const Meetings = React.lazy(() => import("features/meetings/Meetings"))
const MeetingDeliveryNotesView = React.lazy(() => import("features/meetings/delivery-notes-view/MeetingDeliveryNotesView"))
const Coaching = React.lazy(() => import("features/coach/Coaching"))
const FeatureToggle = React.lazy(() => import("features/dev-settings/feature-toggle/FeatureToggle"))
const Templates = React.lazy(() => import("features/templates/Templates"))
const Queues = React.lazy(() => import("features/queues/Queues"))

/** Mirrors OrgGuardStore.hasFeatureToggleAccess('meetings') without needing the store. */
export const canAccessMeetings = (userOrg: UserOrgDto.Info): boolean =>
   userOrg.featureToggles.includes('meetings') || hasFeatureToggleBypass(userOrg.userEmail)

export interface RoutePermission {
   featureId?: FeatureEnum
   actionId?: FeatureActionEnum
   onlyForStages?: StageEnum[]
   onlyForOrgs?: OrgTypeEnum[]
   developerOnly?: boolean
   devOrProductTeamOnly?: boolean
   /** Show this route/menu only when this feature toggle is enabled for the org */
   featureToggle?: FeatureToggleEnum
   /** Show this route/menu only when the org has this Chargebee entitlement (fails open for legacy / not-yet-loaded orgs) */
   chargebeeFeatureId?: ChargebeeFeatureId
   /** return true if we are allowed to see */
   additionalConditions?: (userData: UserDto.info) => boolean
}

export interface RouteItem {
   path?: string
   absolutePath?: string
   name: string
   exact: boolean
   component: React.FunctionComponent
   renderTitle?: boolean
   suffix?: string
   /** Allow role to only satisfy one out of a number of permissions */
   permissions?: RoutePermission[]
   canAccessAsADeactivatedUser?: boolean
}

export const routes: Record<RouteType, RouteItem> = {
   ['no-access']: {
      exact: true,
      name: '',
      path: 'no-access',
      component: AccessDenied,
      permissions: [{
         featureId: 'no-access'
      }],
      canAccessAsADeactivatedUser: true
   },
   ['dev-sandbox']: {
      exact: true,
      name: 'Dev Sandbox',
      path: 'dev-sandbox',
      component: DevSandbox,
      permissions: [{
         onlyForStages: ['dryrun', 'dryrun-v3', 'dev', 'preview', 'feature', 'bug'],
         onlyForOrgs: ['gap']
      }]
   },
   ['my-profile']: {
      exact: true,
      name: 'My Profile',
      path: 'my-profile',
      component: MyProfileView,
      canAccessAsADeactivatedUser: true
   },
   ['services']: {
      exact: true,
      name: 'Services',
      path: 'services',
      component: Services,
      permissions: [{
         featureId: 'services',
         actionId: 'maintain',
         onlyForOrgs: ['gap', 'advisor']
      }]
   },
   ['mindsets']: {
      exact: true,
      name: namingConfig.mindset.plural,
      path: 'mindsets',
      component: Mindsets,
      permissions: [{
         featureId: 'mindsets',
         actionId: 'maintain',
         onlyForOrgs: ['gap']
      }]
   },
   ['organisations']: {
      exact: false,
      name: 'Organisations',
      path: 'organisations',
      component: OrgList,
      permissions: [{
         featureId: 'organisations'
      }]
   },
   ['roles']: {
      exact: false,
      name: 'Roles',
      path: 'roles',
      component: Role,
      permissions: [{
         featureId: 'roles'
      }]
   },
   ['background-jobs']: {
      exact: true,
      name: 'Background Jobs',
      path: 'background-jobs',
      component: BackgroundJobListView,
      permissions: [{
         featureId: 'background-jobs'
      }]
   },
   ['document-templates']: {
      exact: false,
      name: namingConfig.documentTemplate.plural,
      path: 'document-templates',
      component: BlueprintOrgTypeView,
      permissions: [{
         featureId: 'blueprints'
      }]
   },
   ['processes']: {
      exact: false,
      name: namingConfig.process.plural,
      path: 'processes',
      component: ProcessList,
      permissions: [{
         featureId: 'processes'
      }]
   },
   ['my-organisation']: {
      exact: false,
      name: 'My Organisation',
      path: `my-organisation`,
      component: OrgEdit,
      suffix: 'general',
      permissions: [{
         featureId: 'my-organisation'
      }]
   },
   ['work-items']: {
      exact: false,
      name: namingConfig.workItem.plural,
      path: 'work-items',
      component: WorkItemView,
      permissions: [{
         featureId: 'work-items',
         additionalConditions: ({ userOrg }) => canSeeWorkItems(userOrg),
      }]
   },
   ['documents']: {
      exact: false,
      name: namingConfig.document.plural,
      path: 'documents',
      component: DocRouter,
      permissions: [{
         featureId: 'documents'
      }]
   },
   ['delivery-notes']: {
      exact: false,
      name: 'Delivery notes',
      path: 'delivery-notes',
      component: DeliveryNotes,
   },
   ['clients']: {
      exact: false,
      name: 'Clients',
      path: 'clients',
      component: Clients,
      permissions: [{
         featureId: 'clients',
         actionId: 'maintain',
         onlyForOrgs: ['advisor']
      }]
   },
   ['proposals']: {
      exact: false,
      name: 'Proposals',
      path: 'proposals',
      component: Proposals,
      permissions: [{
         featureId: 'proposals',
         actionId: 'maintain',
         onlyForOrgs: ['advisor'],
         chargebeeFeatureId: ChargebeeFeatureId.proposals,
      }, {
         onlyForOrgs: ['client'],
      }],
   },
   ['vgc']: {
      exact: false,
      name: 'Value Gap Calculator',
      path: 'vgc',
      component: ValueGapCalculator,
      permissions: [{
         featureId: 'vgc',
         actionId: 'maintain',
         onlyForOrgs: ['advisor'],
         chargebeeFeatureId: ChargebeeFeatureId.valueGapCalculator,
      }]
   },
   ['dev-settings']: {
      exact: false,
      name: 'Dev settings',
      path: 'dev-settings',
      component: DevSettings,
      permissions: [{
         featureId: 'admin-settings',
         developerOnly: true,
         onlyForOrgs: ['gap'],
      }]
   },
   ['feature-toggles']: {
      exact: false,
      name: 'Feature Toggles',
      path: 'feature-toggles',
      component: FeatureToggle,
      permissions: [{
         devOrProductTeamOnly: true,
         onlyForOrgs: ['gap'],
      }]
   },
   ['queues']: {
      exact: false,
      name: 'Queues',
      path: 'queues',
      component: Queues,
      permissions: [queuesPermission]
   },
   ['user-management']: {
      exact: false,
      name: 'User Management',
      path: 'user-management',
      component: UserManagement,
      permissions: [{
         featureId: 'user-management',
         actionId: 'maintain',
         onlyForOrgs: ['gap'],
      }]
   },
   ['ai-prompts']: {
      exact: false,
      name: 'AI Prompts',
      path: 'ai-prompts',
      component: AIPromptView,
      permissions: [{
         featureId: 'admin-settings',
         onlyForOrgs: ['gap']
      }]
   },
   ['meeting-delivery-notes']: {
      exact: true,
      name: 'Meeting Delivery Notes',
      path: 'meetings/:meetingId/delivery-notes',
      component: MeetingDeliveryNotesView,
      renderTitle: false,
      permissions: [{
         onlyForOrgs: ['advisor'],
         featureToggle: 'meetings',
      }]
   },
   ['meetings']: {
      exact: false,
      name: 'Meetings',
      path: 'meetings',
      component: Meetings,
      permissions: [{
         onlyForOrgs: ['advisor'],
         featureToggle: 'meetings',
      }]
   },
   ['coach']: {
      exact: true,
      name: 'Coaching',
      path: 'coach',
      component: Coaching,
      permissions: [{
         onlyForOrgs: ['advisor'],
         featureToggle: 'meetings',
      }]
   },
   ['templates']: {
      exact: false,
      name: 'Templates',
      path: 'templates',
      component: Templates,
      renderTitle: false,
      permissions: [{
         featureId: 'blueprints',
         onlyForOrgs: ['advisor'],
      }]
   },
}

const getAdvisorDefaultRoute = (userOrg: UserOrgDto.Info): RouteItem => {
   if (canAccessMeetings(userOrg)) {
      return routes['meetings']
   }
   if (canSeeWorkItems(userOrg) && userOrg.features.some(feature => feature.featureId === 'work-items')) {
      return routes['work-items']
   }
   return routes['clients']
}

export const getDefaultRoutes = (userOrg: UserOrgDto.Info): Record<OrgTypeEnum, RouteItem> => {
   // active must be set and set to false.
   // If this gets deployed, active may not be set just yet in the cache, so when you see this after 1sept2024, you can remove it
   if (userOrg.isDeactivated) {
      return {
         gap: routes['no-access'],
         advisor: routes['no-access'],
         client: routes['no-access'],
      }
   }

   return {
      gap: (() => {
         // Support role only has organisations permission
         if (userOrg.features.find(feature => feature.featureId === 'processes')) {
            return routes['processes']
         }
         return routes['organisations']
      })(),
      advisor: getAdvisorDefaultRoute(userOrg),
      client: routes['documents'],
   }
}
